import React, { useState, useEffect } from "react";
import styled from "styled-components";
import sanityClient from "../../sanityClient";
import campaignImage from "../../assets/images/blueprint.jpg";

// Styled Components
const SectionContainer = styled.section`
  padding: 50px 20px;
  background-color: #f0f9ff;
  text-align: center;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 40px;
`;

const ImageDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const ImageContainer = styled.div`
  flex: 1;

  img {
    width: 100%;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const Description = styled.p`
  flex: 1;
  font-size: 1.2rem;
  color: #555;
  text-align: left;

  @media (min-width: 768px) {
    text-align: left;
  }
`;

const ProgressContainer = styled.div`
  max-width: 600px;
  margin: 0 auto 40px;
`;

const ProgressHeader = styled.p`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 15px;
`;

const ProgressBarWrapper = styled.div`
  background: #ccc;
  border-radius: 10px;
  overflow: hidden;
  height: 30px;
  margin-bottom: 10px;
`;

const ProgressBarFill = styled.div`
  background: var(--hhb-primary);
  height: 100%;
  width: ${({ progress }) => `${progress}%`};
  transition: width 1.5s ease;
`;

const ProgressText = styled.p`
  font-size: 1.1rem;
  color: #333;
`;

const CardSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Card = styled.div`
  flex: 1;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 20px;
  text-align: left;
  display: flex;
  flex-direction: column;

  h3 {
    font-size: 1.8rem;
    color: var(--hhb-darkblue);
    margin-bottom: 15px;
  }

  p,
  li {
    font-size: 1rem;
    color: #555;
    margin-bottom: 10px;
  }

  ul {
    list-style: none;
    padding: 0;
  }

  li {
    background: #f9f9f9;
    border-radius: 5px;
    padding: 10px;
    margin-bottom: 10px;
  }

  a {
    display: inline-block;
    margin-top: 20px;
    padding: 10px 20px;
    background: var(--hhb-primary);
    color: white;
    text-decoration: none;
    border-radius: 5px;
    transition: background-color 0.3s ease;
    align-self: flex-start;

    &:hover {
      background-color: var(--hhb-darkblue);
    }
  }
`;

const CampaignOverviewSection = () => {
  const [progress, setProgress] = useState(0);
  const fundraisingGoal = 100000;
  const [currentAmount, setCurrentAmount] = useState(0);

  useEffect(() => {
    sanityClient
      .fetch(`*[_type == "fundraisingProgress"][0]{ currentAmount }`)
      .then((data) => {
        const progressPercentage = (data.currentAmount / fundraisingGoal) * 100;
        setCurrentAmount(data.currentAmount);
        setProgress(progressPercentage);
      })
      .catch(console.error);
  }, []);

  return (
    <SectionContainer>
      <ContentWrapper>
        <Title>#400in40 Campaign</Title>
        <ImageDescriptionContainer>
          <ImageContainer>
            <img src={campaignImage} alt="400th House Campaign" />
          </ImageContainer>
          <Description>
            Help us raise $100,000 to build Habitat Buffalo's 400th house in our
            40th year! Your donation directly supports affordable housing for
            families in need.
          </Description>
        </ImageDescriptionContainer>

        <ProgressContainer>
          <ProgressHeader>
            We're raising $100,000 to build our 400th house. Your contributions
            make this possible!
          </ProgressHeader>
          <ProgressBarWrapper>
            <ProgressBarFill progress={progress} />
          </ProgressBarWrapper>
          <ProgressText>
            Raised ${currentAmount.toLocaleString()} of $
            {fundraisingGoal.toLocaleString()}!
          </ProgressText>
        </ProgressContainer>

        <CardSection>
          <Card>
            <h3>Donate $100 - Get an Oxford Pennant!</h3>
            <p>
              Support our mission and receive a limited-edition, commemorative
              Oxford Pennant!
            </p>
            <ul>
              <li>Build safe, affordable homes for families in need.</li>
              <li>
                Help fund critical materials like walls, windows, and roofing.
              </li>
              <li>Support sustainable community development in Buffalo.</li>
            </ul>
            <a href="https://habitatbuffalo.betterworld.org/campaigns/400in40">
              Donate & Get Your Pennant
            </a>
          </Card>

          <Card>
            <h3>Become a Weekly Sponsor - $1,000</h3>
            <p>Match donations for a week and make double the impact.</p>
            <ul>
              <li>
                Receive a commemorative plaque featuring an illustrated
                watercolor by local artist Mickey Harmon.
              </li>
              <li>Recognition on our website and at the build site.</li>
              <li>Social media highlights with a dedicated feature.</li>
              <li>
                Be part of a matching donation challenge to inspire more giving.
              </li>
            </ul>
            <a href="https://habitatbuffalo.betterworld.org/campaigns/400in40">
              Become a Sponsor
            </a>
          </Card>
        </CardSection>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default CampaignOverviewSection;
